import { DateTime } from "luxon"
import { ModalidadGrupoTarifa, Tarifa } from "src/gql/schema"
import { GrupoTarifa } from "../useMotorTarifas.type"
import { getPrioridad } from "./prioridad"
import { times } from "src/shared/helpers/calculator"

export function getGruposTarifas(
    tarifas_seleccionadas: Tarifa[],
    fecha_inicio: Date,
    fecha_fin: Date,
    zonehotel: string
): GrupoTarifa[] {
    // Validación: si alguna de las fechas es inválida, retorna vacío
    if (!fecha_inicio || !fecha_fin) return []

    // Validación: si el rango de fechas es inválido, retorna vacío
    if (fecha_inicio > fecha_fin) return []

    // Constante para representar un día en milisegundos
    const MS_DIA = 1000 * 60 * 60 * 24

    // Mapeo de los días de la semana para acceder a precios por día
    // Luxon weekday: 1=Lun...7=Dom → % 7 → 0=Dom...6=Sáb (igual que Date.getDay)
    const dias_semana = [
        "domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"
    ]

    // Normaliza las fechas de las tarifas a medianoche del hotel (las fechas del DB vienen en UTC hotel)
    const tarifas_preparadas = tarifas_seleccionadas.map(t => ({
        ...t,
        ...(t?.grupo_tarifa?.modalidad === ModalidadGrupoTarifa.Base
            ? { inicio: fecha_inicio, fin: fecha_fin }
            : {
                inicio: t?.fecha_inicio
                    ? DateTime.fromISO(t.fecha_inicio.substring(0, 10), { zone: zonehotel }).toJSDate()
                    : fecha_inicio,
                fin: t?.fecha_final
                    ? DateTime.fromISO(t.fecha_final.substring(0, 10), { zone: zonehotel }).toJSDate()
                    : fecha_fin
            }
        )
    }))

    // Arreglo donde se almacenan los resultados diarios
    const dias: any[] = []

    /**
     * Función que resuelve la tarifa aplicable para un día específico.
     * Aplica fallback por prioridad: toma la primera tarifa con precio > 0.
     * Si ninguna cumple, retorna la de mayor prioridad con precio 0.
     */
    function resolverTarifaConFallback(tarifas_validas: Tarifa[], dia: string) {

        for (const t of tarifas_validas) {

            const precioDia = Number(t?.dias_disponibles?.[dia] || 0)
            const modalidad = t?.grupo_tarifa?.modalidad || ""

            // Para tarifas adicionales sin precio por día, usar costo_habitacion como precio diario
            const precio = precioDia > 0
                ? precioDia
                : modalidad === ModalidadGrupoTarifa.Adicional
                    ? Number(t?.costo_habitacion || 0)
                    : 0

            // Retorna la primera tarifa válida con precio mayor a 0
            if (precio > 0) {
                return {
                    precio,
                    tarifa: t
                }
            }
        }

        // Si ninguna tarifa tiene precio válido, retorna la primera (mayor prioridad)
        return {
            precio: 0,
            tarifa: tarifas_validas[0] ?? null
        }
    }

    // Itera día por día usando Luxon en zona del hotel para obtener el día correcto
    let current = DateTime.fromJSDate(fecha_inicio).setZone(zonehotel)
    const fin_date = DateTime.fromJSDate(fecha_fin).setZone(zonehotel)

    while (current.toMillis() <= fin_date.toMillis()) {

        const fecha_actual = current.toJSDate()

        // Luxon weekday: 1=Lun...7=Dom → % 7 → 0=Dom...6=Sáb
        const dia_nombre = dias_semana[current.weekday % 7]

        // Filtra tarifas que:
        // - Están dentro del rango de vigencia
        // - Tienen definido un valor para ese día
        const tarifas_validas = tarifas_preparadas.filter(t =>
            fecha_actual >= t.inicio &&
            fecha_actual <= t.fin &&
            ![0, undefined].includes(t?.dias_disponibles?.[dia_nombre])
        )

        // Ordena las tarifas por prioridad (de mayor a menor)
        tarifas_validas.sort((a, b) => getPrioridad(b) - getPrioridad(a))

        // Resuelve la tarifa aplicable usando fallback
        const { precio, tarifa } = resolverTarifaConFallback(
            tarifas_validas,
            dia_nombre
        )

        // Guarda el resultado del día
        dias.push({
            fecha: fecha_actual,
            precio,
            tarifa_id: tarifa?.tarifa_id,
            tarifa
        })

        // Avanza al siguiente día en zona del hotel
        current = current.plus({ days: 1 })
    }

    // Si no hay días procesados, retorna vacío
    if (!dias.length) return []

    // Arreglo final de grupos de tarifas
    const grupos: GrupoTarifa[] = []

    // Marca el inicio del grupo actual
    let inicio_grupo = dias?.[0]

    /**
     * Función que cierra un grupo de días consecutivos con la misma tarifa
     * y calcula sus métricas (días, subtotal, etc.)
     */
    function cerrarGrupo(inicio: any, fin: any) {

        const fecha_inicio_g = new Date(inicio.fecha)
        const fecha_fin_g = new Date(fin.fecha)

        // Calcula la cantidad de días en el grupo (incluyente)
        const dias_grupo =
            ((fecha_fin_g.getTime() - fecha_inicio_g.getTime()) / MS_DIA) + 1

        // Calcula el subtotal (precio por día * número de días)
        const subtotal = times(Number(fin.precio ?? 0), dias_grupo)

        // Agrega el grupo al resultado final
        grupos.push({
            fecha_inicio: fecha_inicio_g.toISOString(),
            fecha_fin: fecha_fin_g.toISOString(),
            dias: dias_grupo,
            costo_hospedaje: fin.precio,
            subtotal_hospedaje: subtotal,
            tarifa_id: fin.tarifa_id,
            tarifa: fin.tarifa
        })
    }

    // Recorre los días para agrupar por cambios de precio o tarifa
    for (let i = 1; i < dias.length; i++) {
        const actual = dias[i]
        const anterior = dias[i - 1]

        // Si cambia el precio o la tarifa, se cierra el grupo actual
        if (
            actual.precio !== anterior.precio ||
            actual.tarifa_id !== anterior.tarifa_id
        ) {
            cerrarGrupo(inicio_grupo, anterior)
            inicio_grupo = actual
        }
    }

    // Cierra el último grupo
    cerrarGrupo(inicio_grupo, dias[dias.length - 1])

    // Retorna los grupos de tarifas calculados
    return grupos
}